import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EnvironmentProviders,
  inject,
  input,
  makeEnvironmentProviders,
} from '@angular/core';

import { type MinimalMember, toMemberBrief } from '@core/ask-hr-smart-ui';
import type { AskTicketBoardItem, AskTicketPriority, AskTicketStatus } from '@core/ask-hr-types';
import type { SymbolEmoji } from '@core/symbol-legacy';
import {
  type ChatPresenterComponent,
  provideTellentAssistantRenderPresenter,
} from '@core/tellent-assistant-contracts';
import { AppKind, ORG_GUID } from '@core/tellent-types';
import { injectTellentAppUrlBuilder } from '@core/tellent-util';

import { AskTicketItemComponent } from '../ask-ticket-item/ask-ticket-item.component';

interface RequestTicketArgs {
  id: number;
  name: string;
  status: AskTicketStatus;
  createdAt: string;
  priority?: AskTicketPriority | null;
  symbol?: SymbolEmoji | null;
  requester: MinimalMember;
  assignees?: MinimalMember[];
}

@Component({
  selector: 'tellent-assistant-ticket-renderer',
  imports: [AskTicketItemComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    :host {
      display: block;
    }
  `,
  template: `<ask-ticket-item [ticket]="ticket()" [redirectUrl]="url()" layout-full-width />`,
})
export class TellentAssistantTicketRendererComponent implements ChatPresenterComponent<RequestTicketArgs> {
  private readonly buildAppUrl = injectTellentAppUrlBuilder();
  private readonly orgGuid = inject(ORG_GUID, { optional: true });

  protected readonly ticket = computed<AskTicketBoardItem>(() => {
    const args = this.args();

    return {
      id: args.id,
      name: args.name,
      status: args.status,
      priority: args.priority ?? null,
      symbol: args.symbol ?? null,
      createdAt: args.createdAt,
      updatedAt: args.createdAt,
      requester: toMemberBrief(args.requester),
      assignees: (args.assignees ?? []).map(toMemberBrief),
    };
  });

  protected readonly url = computed(() =>
    this.buildAppUrl(
      AppKind.KiwiHr,
      { id: 'ask:ticket', params: { ticket_id: this.args().id } },
      this.orgGuid ?? undefined,
    ),
  );

  public readonly args = input.required<RequestTicketArgs>();
}

export function provideTellentTicketCardRenderer(): EnvironmentProviders {
  return makeEnvironmentProviders([
    provideTellentAssistantRenderPresenter<RequestTicketArgs>({
      id: 'presentRequestTicket',
      component: TellentAssistantTicketRendererComponent,
    }),
  ]);
}
